import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { Input } from '../../../../../components/ui/input';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Plus, X } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentTag } from '../../../types';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { getPreSignedUrlToUpload } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { bulkCreateUpdateRegistry } from '../../../../../redux/Project/registrySaga';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { AntdDate } from '../../../../../components/ui/antd-date';
import dayjs from 'dayjs';
import { refreshEquipmentData } from '../../../../../redux/Project/registryData/action';

interface AddEquipmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: string) => void;
  equipmentType: string;
  project: DashboardProject;
  equipmentToEdit?: any;
}

export const AddEquipmentModal: React.FC<AddEquipmentModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  equipmentType: initialEquipmentType,
  project,
  equipmentToEdit
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!equipmentToEdit;
  const [equipmentType, setEquipmentType] = useState<string>(initialEquipmentType);
  const [equipmentName, setEquipmentName] = useState('');
  const [equipmentNumber, setEquipmentNumber] = useState('');
  const [equipmentNotes, setEquipmentNotes] = useState('');
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [registryItems, setRegistryItems] = useState<any[]>([]);
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [calibrationStatuses, setCalibrationStatuses] = useState<any[]>([]);
  const [certificateNumbers, setCertificateNumbers] = useState<any[]>([]);
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingModel, setIsAddingModel] = useState(false);
  const [isAddingCalibrationStatus, setIsAddingCalibrationStatus] = useState(false);
  const [isAddingCertificateNumber, setIsAddingCertificateNumber] = useState(false);

  // Equipment registry fields
  const [serialNumber, setSerialNumber] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string[]>([]);
  const [model, setModel] = useState('');
  const [calibrationDate, setCalibrationDate] = useState<string>('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [selectedCalibrationCompany, setSelectedCalibrationCompany] = useState<string[]>([]);
  const [calibrationStatus, setCalibrationStatus] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSpoolProject = Boolean(project.project?.projectTypes?.fabrication?.options?.spools?.selected);

  // Load document tags, registry items, manufacturers, and locations on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (tags && tags.length > 0) {
          const equipmentTags = tags.filter((tag: any) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS');
          setDocumentTags(equipmentTags);

          // Load manufacturers (assuming they're in the tags with type MANUFACTURER or similar)
          const manufacturerTags = tags.filter((tag: any) =>
            tag.tags_type === 'MANUFACTURER' ||
            tag.tags_type === 'MANUFACTURER_TAGS' ||
            tag.tags_type?.toLowerCase().includes('manufacturer')
          );
          setManufacturers(manufacturerTags);

          // Load locations
          const locationTags = tags.filter((tag: any) => tag.tags_type === 'MATERIAL_LOCATION_TAGS');
          setLocations(locationTags);
          
          // Load models (assuming they're in tags with type EQUIPMENT_MODEL or similar)
          const modelTags = tags.filter((tag: any) => 
            tag.tags_type === 'EQUIPMENT_MODEL' || 
            tag.tags_type === 'EQUIPMENT_MODEL_TAGS' ||
            tag.tags_type?.toLowerCase().includes('model')
          );
          setModels(modelTags);
          
          // Load calibration statuses (assuming they're in tags with type CALIBRATION_STATUS or similar)
          const statusTags = tags.filter((tag: any) => 
            tag.tags_type === 'CALIBRATION_STATUS' || 
            tag.tags_type === 'CALIBRATION_STATUS_TAGS' ||
            tag.tags_type?.toLowerCase().includes('calibration_status')
          );
          
          // Remove duplicates based on normalized tags_name (case-insensitive, trimmed, no trailing periods)
          const normalizeName = (name: string) => {
            return name?.trim().replace(/\.+$/, '').toLowerCase() || '';
          };
          
          const uniqueStatusTags = statusTags.filter((tag: any, index: number, self: any[]) => {
            const normalizedName = normalizeName(tag.tags_name);
            // Keep first occurrence of each unique normalized name
            return self.findIndex((t: any) => normalizeName(t.tags_name) === normalizedName) === index;
          });
          
          setCalibrationStatuses(uniqueStatusTags);
          
          // Load certificate numbers (assuming they're in tags with type CERTIFICATE_NUMBER or similar)
          const certificateTags = tags.filter((tag: any) => 
            tag.tags_type === 'CERTIFICATE_NUMBER' || 
            tag.tags_type === 'CERTIFICATE_NUMBER_TAGS' ||
            tag.tags_type?.toLowerCase().includes('certificate')
          );
          setCertificateNumbers(certificateTags);
        }
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    const loadRegistryItems = async () => {
      try {
        // const items = await getListMaterialCategory('Registry item');
        // if (items && items.length > 0) {
        //   setRegistryItems(items);
        // }

        const defaultItems: any[] = [
          { tags_id: 1, tags_name: 'Welding', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 2, tags_name: 'NDE', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 3, tags_name: 'QC', tags_type: 'REGISTRY_ITEM' },
          { tags_id: 4, tags_name: 'Coating', tags_type: 'REGISTRY_ITEM' },
          ...(isSpoolProject
            ? [
                { tags_id: 5, tags_name: 'Hydro Testing/Flushing of pipe', tags_type: 'REGISTRY_ITEM' },
                { tags_id: 6, tags_name: 'Torquing', tags_type: 'REGISTRY_ITEM' },
                { tags_id: 7, tags_name: 'Gauging of pipe', tags_type: 'REGISTRY_ITEM' },
              ]
            : []),
        ];
        setRegistryItems(defaultItems);
      } catch (error) {
        console.error('Failed to load registry items:', error);
      }
    };

    loadTags();
    loadRegistryItems();
  }, [isSpoolProject]);

  // Get tags for equipment and consumable
  const getTags = () => {
    return documentTags.filter((tag: any) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS');
  };

  // Convert document tags to options for the MultiSelect component
  const tagOptions = useMemo(() =>
    getTags().map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    })), [documentTags]
  );

  // Convert manufacturers to options for MultiSelect
  const manufacturerOptions = useMemo(() =>
    manufacturers.map(manufacturer => ({
      value: manufacturer.tags_id?.toString() || manufacturer.manufacturerId?.toString() || '',
      label: manufacturer.tags_name || manufacturer.manufacturerName || ''
    })).filter(opt => opt.value && opt.label), [manufacturers]
  );

  // Convert locations to options for SearchableSelect
  const locationOptions = useMemo(() =>
    locations.map(location => ({
      id: location.tags_id?.toString() || '',
      name: location.tags_name || ''
    })).filter(opt => opt.id && opt.name), [locations]
  );

  // Convert models to options for SearchableSelect
  const modelOptions = useMemo(() =>
    models.map(model => ({
      id: model.tags_id?.toString() || '',
      name: model.tags_name || ''
    })).filter(opt => opt.id && opt.name), [models]
  );

  // Convert calibration statuses to options for SearchableSelect
  const calibrationStatusOptions = useMemo(() =>
    calibrationStatuses.map(status => ({
      id: status.tags_id?.toString() || '',
      name: status.tags_name || ''
    })).filter(opt => opt.id && opt.name), [calibrationStatuses]
  );

  // Convert certificate numbers to options for SearchableSelect
  const certificateNumberOptions = useMemo(() =>
    certificateNumbers.map(cert => ({
      id: cert.tags_id?.toString() || '',
      name: cert.tags_name || ''
    })).filter(opt => opt.id && opt.name), [certificateNumbers]
  );

  // Initialize state when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (equipmentToEdit) {
        // Edit mode - populate with existing data from top level (not extra_details)
        setEquipmentType(equipmentToEdit.equipment_type || equipmentToEdit.equipmentType || initialEquipmentType);
        setEquipmentName(equipmentToEdit.equipment_name || equipmentToEdit.equipmentName || '');
        setEquipmentNumber(equipmentToEdit.equipment_number || equipmentToEdit.equipmentNumber || '');
        setEquipmentNotes(equipmentToEdit.notes || '');
        setSerialNumber(equipmentToEdit.serial_number || '');
        setModel(equipmentToEdit.model || '');
        setCalibrationStatus(equipmentToEdit.calibration_status || '');
        setSelectedLocation(equipmentToEdit.location || '');
        setCalibrationDate(equipmentToEdit.calibration_date || '');
        setExpiryDate(equipmentToEdit.expiry_date || '');
        setMaterialDocuments([]);
        setSelectedTags([]);
      } else {
        // Add mode - reset to empty
        setEquipmentType(initialEquipmentType);
        setEquipmentName('');
        setEquipmentNumber('');
        setEquipmentNotes('');
        setSerialNumber('');
        setModel('');
        setCertificateNumber('');
        setCalibrationStatus('');
        setSelectedLocation('');
        setSelectedManufacturer([]);
        setSelectedCalibrationCompany([]);
        setCalibrationDate('');
        setExpiryDate('');
        setMaterialDocuments([]);
        setSelectedTags([]);
      }
    }
  }, [isOpen, initialEquipmentType, equipmentToEdit]);

  // Set dropdown values after options are loaded (for edit mode)
  useEffect(() => {
    if (isOpen && equipmentToEdit) {
      // Set Certificate Number - find matching option by name
      if (equipmentToEdit.certificate_number && certificateNumberOptions.length > 0) {
        const matchingCert = certificateNumberOptions.find(
          opt => opt.name.toLowerCase() === equipmentToEdit.certificate_number?.toLowerCase()
        );
        if (matchingCert) {
          setCertificateNumber(matchingCert.name);
        } else {
          // If not found in options, set the value directly (might be a new value not in dropdown yet)
          setCertificateNumber(equipmentToEdit.certificate_number);
        }
      } else if (equipmentToEdit.certificate_number) {
        setCertificateNumber(equipmentToEdit.certificate_number);
      }

      // Set Manufacturer - find IDs from manufacturer names
      if (equipmentToEdit.manufacturer && manufacturerOptions.length > 0) {
        const manufacturerNames = equipmentToEdit.manufacturer.split(',').map((name: string) => name.trim());
        const matchingIds = manufacturerNames
          .map((name: string) => {
            const option = manufacturerOptions.find(opt => 
              opt.label.toLowerCase() === name.toLowerCase()
            );
            return option?.value;
          })
          .filter(Boolean) as string[];
        setSelectedManufacturer(matchingIds);
      } else if (equipmentToEdit.manufacturerIds && manufacturerOptions.length > 0) {
        // Fallback: if manufacturerIds exist, use them
        const validManufacturerIds = (equipmentToEdit.manufacturerIds || [])
          .map((id: number) => id.toString())
          .filter((id: string) => manufacturerOptions.some(opt => opt.value === id));
        setSelectedManufacturer(validManufacturerIds);
      }

      // Set Calibration Company - find IDs from calibration_company names
      if (equipmentToEdit.calibration_company && manufacturerOptions.length > 0) {
        const calibrationCompanyNames = equipmentToEdit.calibration_company.split(',').map((name: string) => name.trim());
        const matchingIds = calibrationCompanyNames
          .map((name: string) => {
            const option = manufacturerOptions.find(opt => 
              opt.label.toLowerCase() === name.toLowerCase()
            );
            return option?.value;
          })
          .filter(Boolean) as string[];
        setSelectedCalibrationCompany(matchingIds);
      } else if (equipmentToEdit.calibration_companyIds && manufacturerOptions.length > 0) {
        // Fallback: if calibration_companyIds exist, use them
        const validCalibrationCompanyIds = (equipmentToEdit.calibration_companyIds || [])
          .map((id: number) => id.toString())
          .filter((id: string) => manufacturerOptions.some(opt => opt.value === id));
        setSelectedCalibrationCompany(validCalibrationCompanyIds);
      }
    }
  }, [isOpen, equipmentToEdit, certificateNumberOptions, manufacturerOptions]);

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setMaterialDocuments(prev => [...prev, ...fileArray]);
      // Initialize tags array for new files
      const newTags = Array(fileArray.length).fill([]);
      setSelectedTags(prev => [...prev, ...newTags]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    setSelectedTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = [...selectedTags];
    updatedTags[fileIndex] = tags;
    setSelectedTags(updatedTags);
  };

  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      if (getTags().find((tag: any) => tag.tags_name === tagName.trim())) {
        customNotification.warning(`${tagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: tagName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload);

      if (res) {
        const updatedTags = [...documentTags, res];
        setDocumentTags(updatedTags);
        setIsAddingTag(false);
      }
    }
  };

  const handleAddManufacturer = async (manufacturerName: string) => {
    if (manufacturerName.trim()) {
      if (manufacturerOptions.find((opt: any) => opt.label.toLowerCase() === manufacturerName.trim().toLowerCase())) {
        customNotification.warning(`${manufacturerName.trim()} manufacturer already exists`);
        return;
      }

      const apiPayload = {
        tags_name: manufacturerName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'MANUFACTURER',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Manufacturer');

      if (res) {
        const updatedManufacturers = [...manufacturers, res];
        setManufacturers(updatedManufacturers);
        setIsAddingManufacturer(false);
      }
    }
  };

  const handleAddLocation = async (locationName: string) => {
    if (locationName.trim()) {
      if (locationOptions.find((opt: any) => opt.name.toLowerCase() === locationName.trim().toLowerCase())) {
        customNotification.warning(`${locationName.trim()} location already exists`);
        return;
      }

      const apiPayload = {
        tags_name: locationName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'MATERIAL_LOCATION_TAGS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Location Tag');

      if (res) {
        const updatedLocations = [...locations, res];
        setLocations(updatedLocations);
        setIsAddingLocation(false);
      }
    }
  };

  const handleAddModel = async (modelName: string) => {
    if (modelName.trim()) {
      if (modelOptions.find((opt: any) => opt.name.toLowerCase() === modelName.trim().toLowerCase())) {
        customNotification.warning(`${modelName.trim()} model already exists`);
        return;
      }

      const apiPayload = {
        tags_name: modelName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'EQUIPMENT_MODEL',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Equipment Model');

      if (res) {
        const updatedModels = [...models, res];
        setModels(updatedModels);
        setIsAddingModel(false);
        // Set the newly added model as selected
        setModel(modelName.trim());
      }
    }
  };

  const handleAddCalibrationStatus = async (statusName: string) => {
    if (statusName.trim()) {
      if (calibrationStatusOptions.find((opt: any) => opt.name.toLowerCase() === statusName.trim().toLowerCase())) {
        customNotification.warning(`${statusName.trim()} calibration status already exists`);
        return;
      }

      const apiPayload = {
        tags_name: statusName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'CALIBRATION_STATUS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Calibration Status');

      if (res) {
        const updatedStatuses = [...calibrationStatuses, res];
        setCalibrationStatuses(updatedStatuses);
        setIsAddingCalibrationStatus(false);
        // Set the newly added status as selected
        setCalibrationStatus(statusName.trim());
      }
    }
  };

  const handleAddCertificateNumber = async (certNumber: string) => {
    if (certNumber.trim()) {
      if (certificateNumberOptions.find((opt: any) => opt.name.toLowerCase() === certNumber.trim().toLowerCase())) {
        customNotification.warning(`${certNumber.trim()} certificate number already exists`);
        return;
      }

      const apiPayload = {
        tags_name: certNumber.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'CERTIFICATE_NUMBER',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Certificate Number');

      if (res) {
        const updatedCertNumbers = [...certificateNumbers, res];
        setCertificateNumbers(updatedCertNumbers);
        setIsAddingCertificateNumber(false);
        // Set the newly added certificate number as selected
        setCertificateNumber(certNumber.trim());
      }
    }
  };

  // Get projectTypeId from project - use first available project type
  const getProjectTypeId = (): number => {
    // Try to get projectTypeId from project types
    const projectTypes = project.project?.projectTypes;
    if (projectTypes) {
      // Try pipeline first
      if (projectTypes.pipeline?.project_type_id) {
        return Number(projectTypes.pipeline.project_type_id);
      }
      // Try fabrication
      if (projectTypes.fabrication?.project_type_id) {
        return Number(projectTypes.fabrication.project_type_id);
      }
      // Try others
      if (projectTypes.others?.project_type_id) {
        return Number(projectTypes.others.project_type_id);
      }
      // Try fabrication options
      if (projectTypes.fabrication?.options) {
        if (projectTypes.fabrication.options.buckleArrestor?.project_type_id) {
          return Number(projectTypes.fabrication.options.buckleArrestor.project_type_id);
        }
        if (projectTypes.fabrication.options.pipeInPipe?.project_type_id) {
          return Number(projectTypes.fabrication.options.pipeInPipe.project_type_id);
        }
        if (projectTypes.fabrication.options.bulkhead?.project_type_id) {
          return Number(projectTypes.fabrication.options.bulkhead.project_type_id);
        }
        if (projectTypes.fabrication.options.spools?.project_type_id) {
          return Number(projectTypes.fabrication.options.spools.project_type_id);
        }
      }
    }
    // Default fallback (should not happen in normal cases)
    return 0;
  };

  const handleAdd = async () => {
    if (!equipmentName.trim()) {
      customNotification.warning('Please enter equipment name');
      return;
    }

    // if (!equipmentNumber.trim()) {
    //   customNotification.warning('Please enter equipment number');
    //   return;
    // }

    const projectTypeId = getProjectTypeId();
    if (!projectTypeId || projectTypeId === 0) {
      customNotification.error(
        'Invalid Project Type',
        'Unable to determine project type. Please ensure the project is properly configured.'
      );
      return;
    }

    setIsUploading(true);
    try {
      let documents: any[] = [];

      // If documents are uploaded, upload them to S3 first
      if (materialDocuments.length > 0) {
        const payload = {
          pipeId: 0, // Equipment doesn't have pipeId, use 0
          projectId: Number(project.project.projectId),
          projectTypeId: projectTypeId,
          type: 'EQUIPMENT'
        };

        const s3Response = await getPreSignedUrlToUpload(materialDocuments, payload, 1);

        if (s3Response && s3Response.length > 0) {
          documents = s3Response.map((file: any, index: number) => ({
            fileName: file.fileName,
            s3Key: file.s3Key,
            version: 1,
            tags: selectedTags[index] || [],
            uploadedBy: user.email || ''
          }));
        }
      }

      // Prepare equipment data for API - save directly at top level, not in extra_details
      const equipmentItem: any = {
        equipment_name: equipmentName.trim(),
        equipment_number: equipmentNumber.trim(),
        equipment_type: equipmentType,
        project_id: Number(project.project.projectId),
        notes: equipmentNotes?.trim() || '',
        serial_number: serialNumber?.trim() || '',
        manufacturer: selectedManufacturer.length > 0 ?
          selectedManufacturer.map(id => manufacturerOptions.find(opt => opt.value === id)?.label).filter(Boolean).join(', ') || '' : '',
        manufacturerIds: [],
        model: model?.trim() || '',
        calibration_date: calibrationDate || '',
        certificate_number: certificateNumber?.trim() || '',
        expiry_date: expiryDate || '',
        calibration_company: selectedCalibrationCompany.length > 0 ?
          selectedCalibrationCompany.map(id => manufacturerOptions.find(opt => opt.value === id)?.label).filter(Boolean).join(', ') || '' : '',
        calibration_companyIds: [],
        calibration_status: calibrationStatus || '',
        location: selectedLocation || '',
        documents: documents,
        created_by: user.email || ''
      };

      // If editing, include the id for update
      if (isEditMode && equipmentToEdit?.id) {
        equipmentItem.id = equipmentToEdit.id;
      }

      const equipmentData = {
        items: [equipmentItem]
      };

      // Call API to create/update equipment
      const result = await bulkCreateUpdateRegistry(equipmentData);

      if (result) {
        if (isEditMode) {
          customNotification.success(
            'Equipment updated successfully',
            `The ${getEquipmentTypeLabel()} equipment has been updated successfully.`
          );
        } else {
          customNotification.success(
            'Equipment added successfully',
            `The ${getEquipmentTypeLabel()} equipment has been added successfully.`
          );
        }
        // Refresh Redux store data to update the equipment list
        dispatch(refreshEquipmentData({ projectId: project.project.projectId }));
        onAdd(equipmentType);
        handleClose();
      }
    } catch (error) {
      customNotification.error(
        'Failed to add equipment',
        'There was an error adding the equipment. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setEquipmentName('');
    setEquipmentNumber('');
    setEquipmentNotes('');
    setSerialNumber('');
    setModel('');
    setCertificateNumber('');
    setCalibrationStatus('');
    setSelectedLocation('');
    setSelectedManufacturer([]);
    setSelectedCalibrationCompany([]);
    setCalibrationDate('');
    setExpiryDate('');
    setMaterialDocuments([]);
    setSelectedTags([]);
    setIsUploading(false);
    onClose();
  };

  const getEquipmentTypeLabel = () => {
    switch (equipmentType) {
      case 'welding':
        return 'Welding';
      case 'nde':
        return 'NDE';
      case 'qc':
        return 'QC';
      case 'consumable':
        return 'Consumable';
      case 'gas':
        return 'Gas';
      case 'hydro-testing-flushing-of-pipe':
        return 'Hydro Testing/Flushing of pipe';
      case 'torquing':
        return 'Torquing';
      case 'gauging-of-pipe':
        return 'Gauging of pipe';
      default:
        return 'Equipment';
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-[1200px]">
          <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-14">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {isEditMode ? 'Edit' : 'Add'} {getEquipmentTypeLabel()} Equipment
            </DialogTitle>
            <Button
              onClick={handleAdd}
              disabled={isUploading || !equipmentName.trim()}
              loading={isUploading}
              loadingText={isEditMode ? 'Updating...' : 'Adding...'}
              size="sm"
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
            >
              {isEditMode ? 'Update Equipment' : 'Add Equipment'}
            </Button>
          </DialogHeader>

          <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">

            {/* Equipment Type */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="equipmentType">Registry  Type</Label>
              <Select
                value={equipmentType}
                onValueChange={(value: string) => {
                  setEquipmentType(value);
                }}
              >
                <SelectTrigger id="equipmentType" className="w-full">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {registryItems.length > 0 ? (
                    registryItems.map((item) => {
                      const tabValue = item.tags_name.toLowerCase().replace(/\s+/g, '-');
                      return (
                        <SelectItem key={item.tags_id} value={tabValue}>
                          {item.tags_name}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <>
                      <SelectItem value="welding">Welding</SelectItem>
                      <SelectItem value="nde">NDE</SelectItem>
                      <SelectItem value="qc">QC</SelectItem>
                      <SelectItem value="consumable">Consumable</SelectItem>
                      <SelectItem value="gas">Gas</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Equipment Name */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="equipmentName">Equipment type</Label>
              <Input
                id="equipmentName"
                value={equipmentName}
                onChange={(e) => setEquipmentName(e.target.value)}
                placeholder="Enter equipment name"
                className="w-full"
              />
            </div>

            {/* Equipment Number */}
            {/* <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="equipmentNumber">Equipment Number</Label>
              <Input
                id="equipmentNumber"
                value={equipmentNumber}
                onChange={(e) => setEquipmentNumber(e.target.value)}
                placeholder="Enter equipment number"
                className="w-full"
              />
            </div>
            */}

      
            {/* Serial Number */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="serialNumber">Serial Number</Label>
              <Input
                id="serialNumber"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                placeholder="Enter serial number"
                className="w-full"
              />
            </div>

            {/* Manufacturer */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Manufacturer</Label>
              <MultiSelect
                options={manufacturerOptions}
                selected={selectedManufacturer}
                onChange={(value: string[]) => {
                  setSelectedManufacturer(value);
                }}
                placeholder="Select manufacturer"
                isSearchable={true}
                className="w-full"
                showAddButton={true}
                addButtonText="Add New Manufacturer"
                onAddButtonClick={() => setIsAddingManufacturer(true)}
                maxHeight="200px"
              />
            </div>

            {/* Model */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="model">Model</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={model}
                  onValueChange={(value: string) => {
                    setModel(value);
                  }}
                  placeholder="Select model"
                  searchPlaceholder="Search models..."
                  options={modelOptions}
                  onAddNew={() => setIsAddingModel(true)}
                  addNewText="Add New Model"
                />
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="location">Location</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={selectedLocation}
                  onValueChange={(value: string) => {
                    setSelectedLocation(value);
                  }}
                  placeholder="Select location"
                  searchPlaceholder="Search locations..."
                  options={locationOptions}
                  onAddNew={() => setIsAddingLocation(true)}
                  addNewText="Add New Location"
                />
              </div>
            </div>

            {/* Calibration Date */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Calibration Date</Label>
              <div className="flex items-center space-x-2">
                <AntdDate
                  name="calibrationDate"
                  value={calibrationDate}
                  onChange={(value: string) => {
                    setCalibrationDate(value);
                  }}
                />
              </div>
            </div>

            {/* Certificate Number */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="certificateNumber">Certificate Number</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={certificateNumber}
                  onValueChange={(value: string) => {
                    setCertificateNumber(value);
                  }}
                  placeholder="Select certificate number"
                  searchPlaceholder="Search certificate numbers..."
                  options={certificateNumberOptions}
                  onAddNew={() => setIsAddingCertificateNumber(true)}
                  addNewText="Add New Certificate Number"
                />
              </div>
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Expiry Date</Label>
              <div className="flex items-center space-x-2">
                <AntdDate
                  name="expiryDate"
                  value={expiryDate}
                  onChange={(value: string) => {
                    setExpiryDate(value);
                  }}
                />
              </div>
            </div>

            {/* Calibration Company */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Calibration Company</Label>
              <MultiSelect
                options={manufacturerOptions}
                selected={selectedCalibrationCompany}
                onChange={(value: string[]) => {
                  setSelectedCalibrationCompany(value);
                }}
                placeholder="Select calibration company"
                isSearchable={true}
                className="w-full"
                showAddButton={true}
                addButtonText="Add New Company"
                onAddButtonClick={() => setIsAddingManufacturer(true)}
                maxHeight="200px"
              />
            </div>

            {/* Calibration Status */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="calibrationStatus">Calibration Status</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={calibrationStatus}
                  onValueChange={(value: string) => {
                    setCalibrationStatus(value);
                  }}
                  placeholder="Select calibration status"
                  searchPlaceholder="Search statuses..."
                  options={calibrationStatusOptions.length > 0 ? calibrationStatusOptions : []}
                  onAddNew={() => setIsAddingCalibrationStatus(true)}
                  addNewText="Add New Status"
                />
              </div>
            </div>
            <div className="space-y-2 col-span-4">
              <Label className="text-[12px] font-medium text-text-grey">Notes</Label>
              <textarea
                value={equipmentNotes || ''}
                onChange={(e) => setEquipmentNotes(e.target.value)}
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                placeholder="Enter notes..."
              />
            </div>
            {/* Document Upload Section */}
            <div className="space-y-2 col-span-4">
              <Label className="text-[12px] font-medium text-text-grey">Upload Documents</Label>
              <p className="text-[12px] text-heritage-grey">
                Upload the equipment documents.
                <br />
                <span className="text-[11px] text-heritage-grey">
                  Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                  <br />
                  Maximum file size: 100MB per file
                </span>
              </p>

              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple={true}
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags={true}
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                showAddButton={true}
                addButtonText="Add New Tag"
                onAddButtonClick={() => setIsAddingTag(true)}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />

              <div className="text-[11px] text-heritage-grey">
                Current files: {materialDocuments.length} file(s)
              </div>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      {/* Add New Manufacturer Dialog */}
      <AddNewItemDialog
        open={isAddingManufacturer}
        onOpenChange={setIsAddingManufacturer}
        title="Add New Manufacturer"
        label="Manufacturer Name"
        placeholder="Enter manufacturer name"
        onAdd={handleAddManufacturer}
        buttonText="Add Manufacturer"
        cancelText="Cancel"
      />

      {/* Add New Location Dialog */}
      <AddNewItemDialog
        open={isAddingLocation}
        onOpenChange={setIsAddingLocation}
        title="Add New Location"
        label="Location Name"
        placeholder="Enter location name"
        onAdd={handleAddLocation}
        buttonText="Add Location"
        cancelText="Cancel"
      />

      {/* Add New Model Dialog */}
      <AddNewItemDialog
        open={isAddingModel}
        onOpenChange={setIsAddingModel}
        title="Add New Model"
        label="Model Name"
        placeholder="Enter model name"
        onAdd={handleAddModel}
        buttonText="Add Model"
        cancelText="Cancel"
      />

      {/* Add New Calibration Status Dialog */}
      <AddNewItemDialog
        open={isAddingCalibrationStatus}
        onOpenChange={setIsAddingCalibrationStatus}
        title="Add New Calibration Status"
        label="Status Name"
        placeholder="Enter calibration status name"
        onAdd={handleAddCalibrationStatus}
        buttonText="Add Status"
        cancelText="Cancel"
      />

      {/* Add New Certificate Number Dialog */}
      <AddNewItemDialog
        open={isAddingCertificateNumber}
        onOpenChange={setIsAddingCertificateNumber}
        title="Add New Certificate Number"
        label="Certificate Number"
        placeholder="Enter certificate number"
        onAdd={handleAddCertificateNumber}
        buttonText="Add Certificate Number"
        cancelText="Cancel"
      />
    </>
  );
};

